import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows } from "@/lib/adminApi";
import type { Farm, Farmer, FarmerSettlement } from "@/lib/adminModules";
import {
  DataTable,
  ErrorBanner,
  LoadingState,
  PageHeader,
  StatCards,
  StatusPill,
  btnGhost,
  btnPrimary,
  type Column,
  type Tone,
} from "@/pages/admin/components/AdminUI";
import { formatRupees } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { buildFarmPerformance, type FarmPerformanceRow } from "@/pages/admin/dashboard/dashboardData";

type Row = FarmPerformanceRow & { id: number };

const STATUS_TONE: Record<FarmPerformanceRow["status"], Tone> = {
  Active: "primary",
  Limited: "accent",
  Paused: "neutral",
};

export default function AdminFarmPerformanceReport() {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [settlements, setSettlements] = useState<FarmerSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [farmRows, farmerRows, settlementRows] = await Promise.all([
        listRows<Farm>("farms", "name"),
        listRows<Farmer>("farmers", "name"),
        listRows<FarmerSettlement>("farmer_settlements", "final_settlement"),
      ]);
      setFarms(farmRows);
      setFarmers(farmerRows);
      setSettlements(settlementRows);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load farm performance.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo<Row[]>(
    () => buildFarmPerformance(farms, farmers, settlements).map((row, index) => ({ ...row, id: index + 1 })),
    [farms, farmers, settlements],
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          bookings: acc.bookings + row.bookings,
          visitors: acc.visitors + row.visitors,
          revenue: acc.revenue + row.revenue,
        }),
        { bookings: 0, visitors: 0, revenue: 0 },
      ),
    [rows],
  );

  const avgRating = rows.length ? (rows.reduce((sum, row) => sum + row.rating, 0) / rows.length).toFixed(1) : "0.0";

  const columns: Column<Row>[] = [
    { key: "farm", header: "Farm", primary: true, render: (row) => <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farm}</span> },
    { key: "bookings", header: "Bookings", render: (row) => <span className="text-sm text-foreground-700">{row.bookings}</span> },
    { key: "visitors", header: "Visitors", render: (row) => <span className="text-sm text-foreground-700">{row.visitors}</span> },
    { key: "revenue", header: "Revenue", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.revenue)}</span> },
    {
      key: "rating",
      header: "Rating",
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-sm text-foreground-800">
          <i className="ri-star-fill text-accent-500"></i>
          {row.rating}
        </span>
      ),
    },
    {
      key: "utilization",
      header: "Utilization",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[110px]">
          <span className="h-2 w-16 rounded-full bg-background-200 overflow-hidden">
            <span className="block h-full rounded-full bg-primary-500" style={{ width: `${row.utilization}%` }}></span>
          </span>
          <span className="text-xs text-foreground-600">{row.utilization}%</span>
        </div>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill label={row.status} tone={STATUS_TONE[row.status]} /> },
  ];

  const exportColumns = [
    { header: "Farm", value: (row: Row) => row.farm },
    { header: "Bookings", value: (row: Row) => `${row.bookings}` },
    { header: "Visitors", value: (row: Row) => `${row.visitors}` },
    { header: "Revenue", value: (row: Row) => `${row.revenue}` },
    { header: "Rating", value: (row: Row) => `${row.rating}` },
    { header: "Utilization %", value: (row: Row) => `${row.utilization}` },
    { header: "Status", value: (row: Row) => row.status },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Farm Performance"
        subtitle="Every farm ranked by settlement value — bookings, visitors, revenue, rating and utilization side by side."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-farm-performance", exportColumns, rows)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Farm Performance Report", subtitle: `${rows.length} farms ranked by settlement value`, columns: exportColumns, rows })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Farms in view", value: rows.length, icon: "ri-plant-line", tone: "primary" },
          { label: "Total bookings", value: totals.bookings, icon: "ri-calendar-check-line", tone: "primary" },
          { label: "Settlement value", value: formatRupees(totals.revenue), icon: "ri-hand-coin-line", tone: "accent" },
          { label: "Avg. rating", value: avgRating, icon: "ri-star-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5">
        {loading ? (
          <LoadingState label="Ranking farms…" />
        ) : (
          <DataTable columns={columns} rows={rows} />
        )}
      </div>
    </div>
  );
}